import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlassCard, ScoreRing } from '../../components/GlassCard';
import { CareTeamMessages } from '../../components/CareTeamMessages';
import { useUserStore } from '../../store/userStore';
import { DOMAIN_QUESTIONS, DOMAIN_ORDER, STAGE_LABEL, STATUS_STYLE } from '../../data/constants';
import { getLatestAssessment, getDomainCheckins, getDashboard } from '../../lib/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const [assessment, setAssessment] = useState(null);
  const [domainDates, setDomainDates] = useState({});
  const [checkin, setCheckin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const [a, dc, dash] = await Promise.all([
          getLatestAssessment(user.id).catch(() => null),
          getDomainCheckins(user.id, null, 90).catch(() => []),
          getDashboard(user.id).catch(() => ({})),
        ]);
        setAssessment(a);
        setCheckin(dash?.today_checkin || null);
        const latest = {};
        (dc || []).forEach((c) => {
          if (!latest[c.domain]) latest[c.domain] = c.date;
        });
        setDomainDates(latest);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const domainScore = (key) => {
    if (!assessment?.domains) return null;
    const map = { hormonal: 'hormonal_transition', metabolic: 'metabolic_strain', sleep: 'sleep_recovery', mental: 'mental_wellbeing' };
    const d = assessment.domains[map[key]];
    return d ? d.status : null;
  };

  const overall = (() => {
    if (!assessment?.domains) return 74;
    const vals = Object.values(assessment.domains).map((d) => Math.min(100, d.score || 0));
    if (!vals.length) return 74;
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    return Math.max(28, Math.min(94, Math.round(100 - avg * 0.6)));
  })();

  const firstName = (user?.name || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10">
      {/* Greeting */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 animate-fade-up">
        <div>
          <p className="text-[var(--aura-ink-soft)] text-sm">{greet},</p>
          <h1 className="font-display text-3xl sm:text-4xl">{firstName}</h1>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-[var(--aura-purple)]/10 text-[var(--aura-purple)] px-4 py-2 text-sm font-semibold">
          <i className="fa-solid fa-seedling" /> {STAGE_LABEL[user?.menopause_stage] || 'Perimenopause'}
        </div>
      </div>

      {/* Score + insight */}
      <GlassCard className="p-6 sm:p-8 mb-6 animate-fade-up" style={{ animationDelay: '0.05s' }}>
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
          <ScoreRing value={overall} size={148} color="#7C3AED" label="Wellbeing" />
          <div className="flex-1 text-center sm:text-left">
            <h2 className="font-display text-2xl mb-2">Your health picture</h2>
            <p className="text-[var(--aura-ink-soft)] leading-relaxed">
              {assessment?.primary_insight ||
                'Complete your Precision Health Assessment to reveal what\u2019s really driving how you feel \u2014 and how your sleep, hormones, metabolism and mind connect.'}
            </p>
            <div className="flex flex-wrap gap-3 mt-4 justify-center sm:justify-start">
              <button
                data-testid="start-assessment-btn"
                onClick={() => navigate('/assessment')}
                className="aura-btn-gradient text-white font-semibold rounded-full px-5 py-2.5 text-sm flex items-center gap-2"
              >
                <i className="fa-solid fa-wand-magic-sparkles" /> {assessment ? 'Retake assessment' : 'Start assessment'}
              </button>
              {assessment && (
                <button
                  data-testid="view-results-btn"
                  onClick={() => navigate('/assessment/results')}
                  className="rounded-full px-5 py-2.5 text-sm font-semibold border border-[var(--aura-line)] text-[var(--aura-purple)] hover:bg-[var(--aura-purple)]/8 transition-base"
                >
                  View results
                </button>
              )}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
        {[
          { label: 'Daily check-in', icon: 'fa-pen-to-square', to: '/app/track', c: '#14B8A6' },
          { label: 'Biomarkers', icon: 'fa-vial', to: '/biomarkers', c: '#7C3AED' },
          { label: 'Weight & BMI', icon: 'fa-weight-scale', to: '/weight', c: '#EC4899' },
          { label: 'Book consult', icon: 'fa-calendar-check', to: '/app/book', c: '#F97316' },
        ].map((q, i) => (
          <GlassCard
            key={q.label}
            hover
            onClick={() => navigate(q.to)}
            data-testid={`quick-${q.label.replace(/\s+/g, '-').toLowerCase()}`}
            className="p-4 cursor-pointer animate-fade-up"
            style={{ animationDelay: `${0.1 + i * 0.04}s` }}
          >
            <div className="grid place-items-center w-11 h-11 rounded-xl mb-3" style={{ background: `${q.c}18`, color: q.c }}>
              <i className={`fa-solid ${q.icon}`} />
            </div>
            <div className="font-semibold text-sm">{q.label}</div>
          </GlassCard>
        ))}
      </div>

      {/* Care team messages */}
      <div className="mb-8">
        <CareTeamMessages userId={user?.id} />
      </div>

      {/* Domains */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">Health domains</h2>
        <span className="text-xs text-[var(--aura-ink-soft)]">Tap to check in</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {DOMAIN_ORDER.map((key, i) => {
          const d = DOMAIN_QUESTIONS[key];
          const status = domainScore(key);
          const st = status ? STATUS_STYLE[status] : null;
          const last = domainDates[key];
          return (
            <GlassCard
              key={key}
              hover
              onClick={() => navigate(`/questionnaire/${key}`)}
              data-testid={`domain-card-${key}`}
              className="p-5 cursor-pointer animate-fade-up"
              style={{ animationDelay: `${0.12 + i * 0.04}s` }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="grid place-items-center w-12 h-12 rounded-2xl" style={{ background: d.bg, color: d.color }}>
                  <i className={`fa-solid ${d.icon} text-lg`} />
                </div>
                {st && (
                  <span className="text-[11px] font-semibold rounded-full px-2.5 py-1" style={{ color: st.color, background: st.bg }}>
                    {st.label}
                  </span>
                )}
              </div>
              <div className="font-display text-lg">{d.title}</div>
              <div className="text-xs text-[var(--aura-ink-soft)] mt-1">
                {last ? `Last check-in ${last}` : `${d.questions.length} questions`}
              </div>
              <div className="flex items-center justify-between mt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/domain-history/${key}`);
                  }}
                  className="text-xs text-[var(--aura-ink-soft)] hover:text-[var(--aura-purple)]"
                >
                  <i className="fa-solid fa-clock-rotate-left mr-1" /> History
                </button>
                <span className="text-[var(--aura-purple)] text-sm font-semibold">
                  Check in <i className="fa-solid fa-arrow-right ml-1" />
                </span>
              </div>
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
}
